import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT, isFeatureEnabled, LoanFlag, LoanFlagName, LoanRequestItem } from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private readonly _flags = signal<LoanFlag[]>([]);
  readonly flags = this._flags.asReadonly();

  private readonly _processedLoans = signal<Set<string>>(new Set());

  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();

  readonly isLoaded = computed(() => this._processedLoans().size > 0);

  private get melloServBaseUrl(): string {
    return this.env.appSettings.melloServDomainUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async loadFlags(loanRequests: LoanRequestItem[]): Promise<void> {
    const processedLoans = this._processedLoans();
    const newRequests = loanRequests.filter((req) => !processedLoans.has(req.loanNumber));

    if (newRequests.length === 0) {
      return;
    }

    this._isLoading.set(true);

    try {
      const url = `${this.melloServBaseUrl}/${this.apiNamespace}/MelloServDomain/Flags/LoanFlags`;
      const newFlags = await firstValueFrom(this.http.post<LoanFlag[]>(url, newRequests));

      this._flags.update((existing) => [...existing, ...newFlags]);

      this._processedLoans.update((processed) => {
        const updated = new Set(processed);
        newRequests.forEach((req) => updated.add(req.loanNumber));
        return updated;
      });
    } catch (error) {
      this.loggingService.logException(error, 'Failed to load feature flags:');
      throw error;
    } finally {
      this._isLoading.set(false);
    }
  }

  isFlagLoadedForLoan(loanNumber: string): boolean {
    return this._processedLoans().has(loanNumber);
  }

  isFeatureEnabledForLoan(loanNumber: string, featureName: LoanFlagName): boolean {
    const loanFlags = this._flags().filter((f) => f.loanNumber === loanNumber);
    return isFeatureEnabled(loanFlags, featureName);
  }

  isFeatureEnabledForAnyLoan(featureName: LoanFlagName): boolean {
    const allFlags = this._flags();
    return isFeatureEnabled(allFlags, featureName);
  }

  getFlagsForLoan(loanNumber: string): LoanFlag[] {
    return this._flags().filter((f) => f.loanNumber === loanNumber);
  }

  removeFlag(loanNumber: string, featureName: LoanFlagName): void {
    this._flags.update((flags) => flags.filter((f) => !(f.loanNumber === loanNumber && f.name === featureName)));
  }

  async deleteFlagFromServer(
    loanNumber: string,
    flagName: LoanFlagName,
    encryptionValue: string,
    isConsent: boolean,
  ): Promise<void> {
    const url = `${this.melloServBaseUrl}/${this.apiNamespace}/MelloServDomain/Flags/RemoveFlag/${loanNumber}`;

    try {
      await firstValueFrom(this.http.post(url, { flagName, encryptionValue, isConsent }));
    } catch (error) {
      this.loggingService.logException(error, `Failed to delete feature flag ${flagName} for loan ${loanNumber}:`);
    }

    this.removeFlag(loanNumber, flagName);
  }

  clearFlags(): void {
    this._flags.set([]);
    this._processedLoans.set(new Set());
  }
}
